/**
 * Request Report Modal
 * 
 * Complete workflow for requesting credit reports from alternate bureaus via postal mail.
 * Features:
 * - Bureau/source selection
 * - Consumer information collection
 * - Address verification
 * - Letter preview and review
 * - PostGrid submission
 * - Success confirmation
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Database,
  CreditCard,
  Building2,
  Landmark,
  Zap,
  Mail,
  Phone,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  AlertCircle,
  Info,
  Loader2,
  User,
  MapPin,
  Calendar,
  FileText,
  Send,
  Clock,
  X,
  ExternalLink,
  Eye,
  Download,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Source configuration with icons
const SOURCE_ICONS = {
  lexisnexis: Database,
  innovis: CreditCard,
  chexsystems: Landmark,
  corelogic: Building2,
  sagestream: Zap,
};

const SOURCE_COLORS = {
  lexisnexis: 'amber',
  innovis: 'emerald',
  chexsystems: 'blue',
  corelogic: 'purple',
  sagestream: 'cyan',
};

// US States for address selection
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

// ============================================
// STEP 1: SOURCE SELECTION
// ============================================

const SourceSelectionStep = ({ sources, selectedSource, onSelectSource, onContinue }) => {
  return (
    <div data-testid="request-report-step-1">
      <div className="text-center mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Step 1 of 4
        </Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Select Report Source</h2>
        <p className="text-stone-600 text-sm max-w-md mx-auto">
          Choose which bureau or data source you need to request a report from.
        </p>
      </div>

      <div className="grid gap-3 max-h-[400px] overflow-y-auto pr-2">
        {sources.map((source) => {
          const Icon = SOURCE_ICONS[source.id] || Database;
          const isSelected = selectedSource?.id === source.id;
          const color = SOURCE_COLORS[source.id] || 'stone';
          
          return (
            <button
              key={source.id}
              onClick={() => onSelectSource(source)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                isSelected 
                  ? `border-${color}-400 bg-${color}-50 shadow-sm`
                  : 'border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm'
              }`}
              data-testid={`source-${source.id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isSelected ? `bg-${color}-100` : 'bg-stone-100'
                }`}>
                  <Icon size={20} className={isSelected ? `text-${color}-600` : 'text-stone-600'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-stone-900">{source.name}</h4>
                    {isSelected && (
                      <CheckCircle size={16} className="text-emerald-500" />
                    )}
                  </div>
                  <p className="text-sm text-stone-600 line-clamp-2">{source.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-stone-500 flex items-center gap-1">
                      <Clock size={12} />
                      ~{source.typical_response_days} days response
                    </span>
                    {source.online_portal && (
                      <a 
                        href={source.online_portal} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                      >
                        <ExternalLink size={10} />
                        Online option
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex justify-end">
        <Button 
          onClick={onContinue}
          disabled={!selectedSource}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          data-testid="continue-step-1"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 2: CONSUMER INFORMATION
// ============================================

const ConsumerInfoStep = ({ consumerInfo, setConsumerInfo, onBack, onContinue }) => {
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!consumerInfo.first_name?.trim()) newErrors.first_name = 'First name is required';
    if (!consumerInfo.last_name?.trim()) newErrors.last_name = 'Last name is required';
    if (!consumerInfo.ssn_last_four || consumerInfo.ssn_last_four.length !== 4) {
      newErrors.ssn_last_four = 'Last 4 digits of SSN required';
    }
    if (!consumerInfo.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      onContinue();
    }
  };

  const updateField = (field, value) => {
    setConsumerInfo({ ...consumerInfo, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <div data-testid="request-report-step-2">
      <div className="text-center mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Step 2 of 4
        </Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Your Information</h2>
        <p className="text-stone-600 text-sm max-w-md mx-auto">
          Enter the personal information exactly as it appears on your records.
        </p>
      </div>

      <div className="space-y-4">
        {/* Name Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first_name" className="text-sm font-medium text-stone-700">
              First Name *
            </Label>
            <Input
              id="first_name"
              value={consumerInfo.first_name || ''}
              onChange={(e) => updateField('first_name', e.target.value)}
              placeholder="John"
              className={errors.first_name ? 'border-red-300' : ''}
              data-testid="input-first-name"
            />
            {errors.first_name && (
              <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>
            )}
          </div>
          <div>
            <Label htmlFor="last_name" className="text-sm font-medium text-stone-700">
              Last Name *
            </Label>
            <Input
              id="last_name"
              value={consumerInfo.last_name || ''}
              onChange={(e) => updateField('last_name', e.target.value)}
              placeholder="Smith"
              className={errors.last_name ? 'border-red-300' : ''}
              data-testid="input-last-name"
            />
            {errors.last_name && (
              <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>
            )}
          </div>
        </div>

        {/* Middle Name / Suffix Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="middle_name" className="text-sm font-medium text-stone-700">
              Middle Name
            </Label>
            <Input
              id="middle_name"
              value={consumerInfo.middle_name || ''}
              onChange={(e) => updateField('middle_name', e.target.value)}
              placeholder="(Optional)"
              data-testid="input-middle-name"
            />
          </div>
          <div>
            <Label htmlFor="suffix" className="text-sm font-medium text-stone-700">
              Suffix
            </Label>
            <Select
              value={consumerInfo.suffix || 'none'}
              onValueChange={(value) => updateField('suffix', value === 'none' ? '' : value)}
            >
              <SelectTrigger data-testid="select-suffix">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="Jr.">Jr.</SelectItem>
                <SelectItem value="Sr.">Sr.</SelectItem>
                <SelectItem value="II">II</SelectItem>
                <SelectItem value="III">III</SelectItem>
                <SelectItem value="IV">IV</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* SSN / DOB Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ssn_last_four" className="text-sm font-medium text-stone-700">
              SSN Last 4 Digits *
            </Label>
            <Input
              id="ssn_last_four"
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={consumerInfo.ssn_last_four || ''}
              onChange={(e) => updateField('ssn_last_four', e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="1234"
              className={errors.ssn_last_four ? 'border-red-300' : ''}
              data-testid="input-ssn"
            />
            {errors.ssn_last_four && (
              <p className="text-xs text-red-500 mt-1">{errors.ssn_last_four}</p>
            )}
          </div>
          <div>
            <Label htmlFor="date_of_birth" className="text-sm font-medium text-stone-700">
              Date of Birth *
            </Label>
            <Input
              id="date_of_birth"
              type="date"
              value={consumerInfo.date_of_birth || ''}
              onChange={(e) => updateField('date_of_birth', e.target.value)}
              className={errors.date_of_birth ? 'border-red-300' : ''}
              data-testid="input-dob"
            />
            {errors.date_of_birth && (
              <p className="text-xs text-red-500 mt-1">{errors.date_of_birth}</p>
            )}
          </div>
        </div>

        {/* Contact Info (Optional) */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone_number" className="text-sm font-medium text-stone-700">
              Phone Number
            </Label>
            <Input
              id="phone_number"
              type="tel"
              value={consumerInfo.phone_number || ''}
              onChange={(e) => updateField('phone_number', e.target.value)}
              placeholder="(Optional)"
              data-testid="input-phone"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-sm font-medium text-stone-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={consumerInfo.email || ''}
              onChange={(e) => updateField('email', e.target.value)}
              placeholder="(Optional)"
              data-testid="input-email"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-3">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-600 mt-0.5" />
          <div className="text-xs text-blue-800">
            <strong>Privacy Note:</strong> Your SSN and personal information are used only to 
            generate the request letter. We do not store your full SSN.
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="back-step-2">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          data-testid="continue-step-2"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 3: ADDRESS INFORMATION
// ============================================

const AddressStep = ({ address, setAddress, previousAddresses, setPreviousAddresses, onBack, onContinue }) => {
  const [errors, setErrors] = useState({});
  const [showPreviousAddress, setShowPreviousAddress] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!address.address_line1?.trim()) newErrors.address_line1 = 'Street address is required';
    if (!address.city?.trim()) newErrors.city = 'City is required';
    if (!address.state) newErrors.state = 'State is required';
    if (!address.postal_code?.trim() || !/^\d{5}(-\d{4})?$/.test(address.postal_code)) {
      newErrors.postal_code = 'Valid ZIP code required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      onContinue();
    }
  };

  const updateField = (field, value) => {
    setAddress({ ...address, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const addPreviousAddress = () => {
    setPreviousAddresses([...previousAddresses, { address_line1: '', city: '', state: '', postal_code: '' }]);
  };

  const updatePreviousAddress = (index, field, value) => {
    const updated = [...previousAddresses];
    updated[index] = { ...updated[index], [field]: value };
    setPreviousAddresses(updated);
  };

  const removePreviousAddress = (index) => {
    setPreviousAddresses(previousAddresses.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="request-report-step-3">
      <div className="text-center mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Step 3 of 4
        </Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Mailing Address</h2>
        <p className="text-stone-600 text-sm max-w-md mx-auto">
          Enter your current mailing address. The report will be sent here.
        </p>
      </div>

      <div className="space-y-4">
        {/* Street Address */}
        <div>
          <Label htmlFor="address_line1" className="text-sm font-medium text-stone-700">
            Street Address *
          </Label>
          <Input
            id="address_line1"
            value={address.address_line1 || ''}
            onChange={(e) => updateField('address_line1', e.target.value)}
            placeholder="123 Main Street"
            className={errors.address_line1 ? 'border-red-300' : ''}
            data-testid="input-address1"
          />
          {errors.address_line1 && (
            <p className="text-xs text-red-500 mt-1">{errors.address_line1}</p>
          )}
        </div>

        {/* Address Line 2 */}
        <div>
          <Label htmlFor="address_line2" className="text-sm font-medium text-stone-700">
            Apt, Suite, Unit
          </Label>
          <Input
            id="address_line2"
            value={address.address_line2 || ''}
            onChange={(e) => updateField('address_line2', e.target.value)}
            placeholder="(Optional)"
            data-testid="input-address2"
          />
        </div>

        {/* City / State / ZIP */}
        <div className="grid grid-cols-5 gap-4">
          <div className="col-span-2">
            <Label htmlFor="city" className="text-sm font-medium text-stone-700">
              City *
            </Label>
            <Input
              id="city"
              value={address.city || ''}
              onChange={(e) => updateField('city', e.target.value)}
              placeholder="City"
              className={errors.city ? 'border-red-300' : ''}
              data-testid="input-city"
            />
            {errors.city && (
              <p className="text-xs text-red-500 mt-1">{errors.city}</p>
            )}
          </div>
          <div className="col-span-2">
            <Label htmlFor="state" className="text-sm font-medium text-stone-700">
              State *
            </Label>
            <Select
              value={address.state || ''}
              onValueChange={(value) => updateField('state', value)}
            >
              <SelectTrigger className={errors.state ? 'border-red-300' : ''} data-testid="select-state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {US_STATES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && (
              <p className="text-xs text-red-500 mt-1">{errors.state}</p>
            )}
          </div>
          <div className="col-span-1">
            <Label htmlFor="postal_code" className="text-sm font-medium text-stone-700">
              ZIP *
            </Label>
            <Input
              id="postal_code"
              value={address.postal_code || ''}
              onChange={(e) => updateField('postal_code', e.target.value.replace(/[^\d-]/g, '').slice(0, 10))}
              placeholder="12345"
              className={errors.postal_code ? 'border-red-300' : ''}
              data-testid="input-zip"
            />
            {errors.postal_code && (
              <p className="text-xs text-red-500 mt-1">{errors.postal_code}</p>
            )}
          </div>
        </div>

        {/* Previous Addresses (Optional) */}
        <div className="pt-4 border-t border-stone-200">
          <button
            type="button"
            onClick={() => setShowPreviousAddress(!showPreviousAddress)}
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
          >
            {showPreviousAddress ? 'Hide' : 'Add'} Previous Addresses (helps verify identity)
            <ChevronRight size={14} className={showPreviousAddress ? 'rotate-90' : ''} />
          </button>

          {showPreviousAddress && (
            <div className="mt-4 space-y-3">
              {previousAddresses.map((prev, idx) => (
                <div key={idx} className="bg-stone-50 rounded-lg p-3 relative">
                  <button
                    onClick={() => removePreviousAddress(idx)}
                    className="absolute top-2 right-2 text-stone-400 hover:text-red-500"
                  >
                    <X size={14} />
                  </button>
                  <p className="text-xs font-medium text-stone-500 mb-2">Previous Address #{idx + 1}</p>
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      placeholder="Street"
                      value={prev.address_line1 || ''}
                      onChange={(e) => updatePreviousAddress(idx, 'address_line1', e.target.value)}
                      className="col-span-2 text-sm"
                    />
                    <Input
                      placeholder="City"
                      value={prev.city || ''}
                      onChange={(e) => updatePreviousAddress(idx, 'city', e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="State"
                      value={prev.state || ''}
                      onChange={(e) => updatePreviousAddress(idx, 'state', e.target.value.toUpperCase().slice(0, 2))}
                      className="text-sm"
                      maxLength={2}
                    />
                  </div>
                </div>
              ))}
              
              {previousAddresses.length < 3 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addPreviousAddress}
                  className="w-full"
                >
                  + Add Previous Address
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack} data-testid="back-step-3">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={handleContinue}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          data-testid="continue-step-3"
        >
          Continue
          <ChevronRight size={16} className="ml-1" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// STEP 4: REVIEW & SEND
// ============================================

const ReviewSendStep = ({ 
  selectedSource, 
  consumerInfo, 
  address, 
  previousAddresses,
  deliveryMethod,
  setDeliveryMethod,
  onBack, 
  onSend,
  sending,
  letterPreview,
  loadingPreview 
}) => {
  const [showLetterPreview, setShowLetterPreview] = useState(false);
  const fullName = `${consumerInfo.first_name}${consumerInfo.middle_name ? ` ${consumerInfo.middle_name}` : ''} ${consumerInfo.last_name}${consumerInfo.suffix ? ` ${consumerInfo.suffix}` : ''}`;

  return (
    <div data-testid="request-report-step-4">
      <div className="text-center mb-6">
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-3">
          Step 4 of 4
        </Badge>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Review & Send</h2>
        <p className="text-stone-600 text-sm max-w-md mx-auto">
          Review your request details before sending.
        </p>
      </div>

      <div className="space-y-4">
        {/* Source Summary */}
        <div className="bg-stone-50 rounded-xl p-4 border border-stone-200">
          <div className="flex items-start gap-3">
            {(() => {
              const Icon = SOURCE_ICONS[selectedSource?.id] || Database;
              return (
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Icon size={20} className="text-amber-600" />
                </div>
              );
            })()}
            <div>
              <h4 className="font-semibold text-stone-900">{selectedSource?.display_name}</h4>
              <p className="text-sm text-stone-600">{selectedSource?.request_type}</p>
            </div>
          </div>
        </div>

        {/* Consumer Info Summary */}
        <div className="bg-white rounded-xl p-4 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <User size={16} className="text-stone-500" />
            <h4 className="font-medium text-stone-900">Your Information</h4>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-stone-500">Name</p>
              <p className="font-medium text-stone-900">{fullName}</p>
            </div>
            <div>
              <p className="text-stone-500">Date of Birth</p>
              <p className="font-medium text-stone-900">{consumerInfo.date_of_birth}</p>
            </div>
            <div>
              <p className="text-stone-500">SSN (Last 4)</p>
              <p className="font-medium text-stone-900">XXX-XX-{consumerInfo.ssn_last_four}</p>
            </div>
            {consumerInfo.phone_number && (
              <div>
                <p className="text-stone-500">Phone</p>
                <p className="font-medium text-stone-900">{consumerInfo.phone_number}</p>
              </div>
            )}
          </div>
        </div>

        {/* Address Summary */}
        <div className="bg-white rounded-xl p-4 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} className="text-stone-500" />
            <h4 className="font-medium text-stone-900">Mailing Address</h4>
          </div>
          <p className="text-sm text-stone-700">
            {address.address_line1}
            {address.address_line2 && <>, {address.address_line2}</>}
            <br />
            {address.city}, {address.state} {address.postal_code}
          </p>
        </div>

        {/* Delivery Method */}
        <div className="bg-white rounded-xl p-4 border border-stone-200">
          <div className="flex items-center gap-2 mb-3">
            <Send size={16} className="text-stone-500" />
            <h4 className="font-medium text-stone-900">Delivery Method</h4>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setDeliveryMethod('mail')}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                deliveryMethod === 'mail'
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-stone-200 bg-white hover:border-stone-300'
              }`}
              data-testid="delivery-mail"
            >
              <div className="flex items-center gap-2 mb-1">
                <Mail size={16} className={deliveryMethod === 'mail' ? 'text-emerald-600' : 'text-stone-500'} />
                <span className="font-medium text-stone-900">Mail</span>
                {deliveryMethod === 'mail' && (
                  <CheckCircle size={14} className="text-emerald-500 ml-auto" />
                )}
              </div>
              <p className="text-xs text-stone-500">Standard mail delivery</p>
            </button>
            
            <button
              disabled
              className="p-3 rounded-lg border-2 border-stone-200 bg-stone-50 text-left opacity-60 cursor-not-allowed"
              data-testid="delivery-fax"
            >
              <div className="flex items-center gap-2 mb-1">
                <Phone size={16} className="text-stone-400" />
                <span className="font-medium text-stone-500">Fax</span>
                <Badge variant="outline" className="text-xs ml-auto">Coming Soon</Badge>
              </div>
              <p className="text-xs text-stone-400">Electronic delivery</p>
            </button>
          </div>
        </div>

        {/* Letter Preview Button */}
        <div className="flex items-center justify-between py-3 px-4 bg-blue-50 rounded-xl border border-blue-200">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Request Letter</span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowLetterPreview(true)}
            className="text-blue-600 border-blue-300"
            data-testid="preview-letter-btn"
          >
            <Eye size={14} className="mr-1" />
            Preview
          </Button>
        </div>

        {/* Estimated Timeline - Updated to not assume days */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Clock size={18} className="text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 mb-1">Timeline Information</h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>Letter processing: After submission to PostGrid</li>
                <li>Delivery: Tracking updates from carrier</li>
                <li>Response: ~{selectedSource?.typical_response_days || 15} days after receipt</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={onBack} disabled={sending} data-testid="back-step-4">
          <ChevronLeft size={16} className="mr-1" />
          Back
        </Button>
        <Button 
          onClick={onSend}
          disabled={sending}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white min-w-[160px]"
          data-testid="send-request-btn"
        >
          {sending ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Sending...
            </>
          ) : (
            <>
              <Send size={16} className="mr-2" />
              Send Request
            </>
          )}
        </Button>
      </div>

      {/* Letter Preview Modal */}
      <Dialog open={showLetterPreview} onOpenChange={setShowLetterPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Letter Preview</DialogTitle>
          </DialogHeader>
          {loadingPreview ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-emerald-500" size={32} />
            </div>
          ) : letterPreview ? (
            <div 
              className="prose prose-sm max-w-none bg-white p-6 border rounded-lg"
              dangerouslySetInnerHTML={{ __html: letterPreview }}
            />
          ) : (
            <p className="text-stone-500 text-center py-8">Unable to load preview</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================
// SUCCESS STATE
// ============================================

const SuccessStep = ({ result, selectedSource, onClose, onViewRequests }) => {
  return (
    <div className="text-center py-6" data-testid="request-success">
      <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
        <CheckCircle size={32} className="text-emerald-600" />
      </div>
      
      <h2 className="text-xl font-bold text-stone-900 mb-2">Request Sent Successfully!</h2>
      <p className="text-stone-600 mb-6 max-w-md mx-auto">
        Your report request has been submitted to {selectedSource?.name}.
      </p>

      <div className="bg-stone-50 rounded-xl p-5 text-left mb-6 max-w-md mx-auto">
        <h4 className="font-semibold text-stone-900 mb-3">Request Details</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-stone-500">Source</span>
            <span className="font-medium text-stone-900">{selectedSource?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Method</span>
            <span className="font-medium text-stone-900 capitalize">{result?.delivery_method || 'Mail'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Status</span>
            <Badge className={result?.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
              {result?.status === 'sent' ? 'Sent' : 'Processing'}
            </Badge>
          </div>
          {result?.tracking_id && (
            <div className="flex justify-between">
              <span className="text-stone-500">Tracking ID</span>
              <span className="font-mono text-xs text-stone-700">{result.tracking_id}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-stone-500">Est. Delivery</span>
            <span className="font-medium text-stone-900">{result?.estimated_delivery_days || 5}-7 days</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Est. Response</span>
            <span className="font-medium text-stone-900">~{result?.estimated_response_days || 15} days</span>
          </div>
        </div>
        
        {result?.message?.includes('test mode') && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle size={14} className="text-amber-600 mt-0.5" />
              <p className="text-xs text-amber-800">
                <strong>Test Mode:</strong> No actual letter was sent. This is a sandbox simulation.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-left mb-6 max-w-md mx-auto">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <strong>What's Next?</strong>
            <p className="mt-1">
              Once you receive your report from {selectedSource?.name}, return to the 
              Alternate Bureau flow and upload it to continue with dispute analysis.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={onClose} data-testid="close-modal-btn">
          Close
        </Button>
        <Button 
          onClick={onViewRequests}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
          data-testid="view-requests-btn"
        >
          View My Requests
        </Button>
      </div>
    </div>
  );
};

// ============================================
// MAIN MODAL COMPONENT
// ============================================

const RequestReportModal = ({ isOpen, onClose, initialSource = null, onSuccess }) => {
  const { token, getAuthHeaders } = useAuth();
  
  // Step state
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Data state
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [consumerInfo, setConsumerInfo] = useState({});
  const [address, setAddress] = useState({});
  const [previousAddresses, setPreviousAddresses] = useState([]);
  const [deliveryMethod, setDeliveryMethod] = useState('mail');
  const [letterPreview, setLetterPreview] = useState('');
  const [result, setResult] = useState(null);

  // Load available sources
  useEffect(() => {
    if (isOpen) {
      loadSources();
    }
  }, [isOpen]);

  // Set initial source if provided
  useEffect(() => {
    if (initialSource && sources.length > 0) {
      const source = sources.find(s => s.id === initialSource);
      if (source) {
        setSelectedSource(source);
        setStep(2); // Skip source selection
      }
    }
  }, [initialSource, sources]);

  const loadSources = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/report-request/sources`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setSources(response.data?.sources || []);
    } catch (error) {
      console.error('Failed to load sources:', error);
      toast.error('Failed to load report sources');
    } finally {
      setLoading(false);
    }
  };

  const loadLetterPreview = async () => {
    if (!selectedSource || !consumerInfo.first_name || !address.address_line1) return;
    
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${API}/report-request/preview-letter`,
        {
          source: selectedSource.id,
          consumer_info: consumerInfo,
          current_address: address,
          previous_addresses: previousAddresses.filter(a => a.address_line1),
          delivery_method: deliveryMethod
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setLetterPreview(response.data?.letter_html || '');
    } catch (error) {
      console.error('Failed to load letter preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Load preview when entering step 4
  useEffect(() => {
    if (step === 4) {
      loadLetterPreview();
    }
  }, [step]);

  const handleSendRequest = async () => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/report-request/send`,
        {
          source: selectedSource.id,
          consumer_info: consumerInfo,
          current_address: address,
          previous_addresses: previousAddresses.filter(a => a.address_line1),
          delivery_method: deliveryMethod
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setResult(response.data);
      setStep(5); // Success state
      
      if (onSuccess) {
        onSuccess(response.data);
      }
      
      toast.success('Report request sent successfully!');
    } catch (error) {
      console.error('Failed to send request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep(1);
    setSelectedSource(null);
    setConsumerInfo({});
    setAddress({});
    setPreviousAddresses([]);
    setDeliveryMethod('mail');
    setLetterPreview('');
    setResult(null);
    onClose();
  };

  const handleViewRequests = () => {
    handleClose();
    // Navigate to requests list if implemented
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="request-report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send size={20} className="text-amber-600" />
            Request Report With Us
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-emerald-500" size={32} />
          </div>
        ) : step === 5 ? (
          <SuccessStep 
            result={result}
            selectedSource={selectedSource}
            onClose={handleClose}
            onViewRequests={handleViewRequests}
          />
        ) : (
          <>
            {/* Progress Indicator */}
            <div className="flex items-center gap-2 mb-6">
              {[1, 2, 3, 4].map((s) => (
                <React.Fragment key={s}>
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                      s < step ? 'bg-emerald-500 text-white' :
                      s === step ? 'bg-amber-500 text-white' :
                      'bg-stone-200 text-stone-500'
                    }`}
                  >
                    {s < step ? <CheckCircle size={16} /> : s}
                  </div>
                  {s < 4 && (
                    <div className={`flex-1 h-1 rounded ${s < step ? 'bg-emerald-500' : 'bg-stone-200'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step Content */}
            {step === 1 && (
              <SourceSelectionStep
                sources={sources}
                selectedSource={selectedSource}
                onSelectSource={setSelectedSource}
                onContinue={() => setStep(2)}
              />
            )}
            {step === 2 && (
              <ConsumerInfoStep
                consumerInfo={consumerInfo}
                setConsumerInfo={setConsumerInfo}
                onBack={() => setStep(1)}
                onContinue={() => setStep(3)}
              />
            )}
            {step === 3 && (
              <AddressStep
                address={address}
                setAddress={setAddress}
                previousAddresses={previousAddresses}
                setPreviousAddresses={setPreviousAddresses}
                onBack={() => setStep(2)}
                onContinue={() => setStep(4)}
              />
            )}
            {step === 4 && (
              <ReviewSendStep
                selectedSource={selectedSource}
                consumerInfo={consumerInfo}
                address={address}
                previousAddresses={previousAddresses}
                deliveryMethod={deliveryMethod}
                setDeliveryMethod={setDeliveryMethod}
                onBack={() => setStep(3)}
                onSend={handleSendRequest}
                sending={sending}
                letterPreview={letterPreview}
                loadingPreview={loadingPreview}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RequestReportModal;
